import { useEffect, useMemo, useState } from "react"
import { colorPaletteOptions, platformColorPalette } from "../../../../constants"
import { capitalize } from "../../../../utility"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { lang } from "../../../../constants/languages"

type PlatformPercent = {
  key: string
  value: number
  percent: number
}

const PlatformShareBar = ({ platformStreamMap }: { platformStreamMap: { [key: string]: number } }) => {
  const [percentageArray, setPercentageArray] = useState<PlatformPercent[]>([])

  const mapToPercentageArray = (map: { [key: string]: number }): PlatformPercent[] => {
    const entries = Object.entries(map)
    const total = entries.reduce((sum, [, v]) => sum + (v || 0), 0)
    if (total === 0 || entries.length === 0) return []
    const result: PlatformPercent[] = []
    let accumulatedPercent = 0
    for (let i = 0; i < entries.length; i++) {
      const [key, value] = entries[i]
      if (i < entries.length - 1) {
        const percent = Number(((value / total) * 100).toFixed(1))
        accumulatedPercent += percent

        result.push({
          key,
          value,
          percent
        })
      } else {
        // Last one = 100 - others_total
        const lastPercent = Number((100 - accumulatedPercent).toFixed(1))
        result.push({
          key,
          value,
          percent: lastPercent
        })
        result.sort((a, b) => b.value - a.value)
      }
    }

    return result
  }

  useEffect(() => {
    const percentArray = mapToPercentageArray(platformStreamMap)
    setPercentageArray(percentArray)
  }, [platformStreamMap])

   const filteredPercentageArray = useMemo(
    () => percentageArray.filter((item) => item.percent > 0.2),
    [percentageArray]
  )

  return (
    <div
      className="w-full flex flex-col gap-2 p-5"
      style={{
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: " var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div className="flex flex-row justify-start">
        <KTitleSpan text={lang.trends.headers.platform_share} fontSize={16} lineHeight="18px" color="#737373" />
      </div>
      <div className="w-full flex flex-row gap-[1px] h-3 mt-5">
        {filteredPercentageArray.map((item, index) => {
          return (
            <div
              key={`progress-share-${index}`}
              style={{
                width: `${item.percent}%`,
                borderBottomLeftRadius: index === 0 ? 100 : 0,
                borderTopLeftRadius: index === 0 ? 100 : 0,
                borderBottomRightRadius: index === filteredPercentageArray.length - 1 ? 100 : 0,
                borderTopRightRadius: index === filteredPercentageArray.length - 1 ? 100 : 0,
                backgroundColor:
                  platformColorPalette[capitalize(item.key) as keyof typeof platformColorPalette] ??
                  colorPaletteOptions[index % colorPaletteOptions.length]
              }}
            />
          )
        })}
      </div>
      <div className="w-full flex flex-row gap-2 mt-5 flex-wrap">
        {percentageArray.map((item, index) => {
          return (
            <div key={`${item.key}`} className="flex flex-row items-center gap-1 mr-2">
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 8,
                  backgroundColor:
                    platformColorPalette[capitalize(item.key) as keyof typeof platformColorPalette] ??
                    colorPaletteOptions[index]
                }}
              />
              <KSpan text={`${capitalize(item.key)}`} color="#111" />
              <div className="flex pt-0.5 ml-1">
                <KSpan text={`${item.percent}%`} fontSize={12} lineHeight="14px" />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PlatformShareBar
